import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, LockIcon, ShoppingCartIcon, TagIcon } from 'lucide-react';
import { CartLineItem } from '../components/cart/CartLineItem';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import { useCart } from '../contexts/CartContext';
import { products } from '../data/products';
import { paymentMethods } from '../data/navigation';
import { formatPrice } from '../utils/currency';
const PROMO = {
  code: 'AMPRA10',
  rate: 0.1,
  max: 250
};
export function Cart() {
  const {
    items,
    subtotal,
    count,
    addItem,
    clearCart
  } = useCart();
  const [code, setCode] = useState('');
  const [applied, setApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [orderNo, setOrderNo] = useState<string | null>(null);
  const discount = applied ? Math.min(Math.round(subtotal * PROMO.rate * 100) / 100, PROMO.max) : 0;
  const total = Math.round((subtotal - discount) * 100) / 100;
  const vat = Math.round((total - total / 1.21) * 100) / 100;
  const inCart = new Set(items.map(i => i.product.id));
  const addOns = products.filter(p => p.category === 'accessories' && !inCart.has(p.id));
  const applyPromo = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim().toUpperCase() === PROMO.code) {
      setApplied(true);
      setPromoError('');
    } else {
      setPromoError('Deze code is niet geldig. Probeer AMPRA10.');
    }
  };
  if (orderNo) {
    return <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-24 text-center" data-id="element-691" data-mp="2x6svgw32etzl">
        <motion.div initial={{
        scale: 0.96,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.24,
        ease: [0.23, 1, 0.32, 1]
      }} data-id="element-692" data-mp="2j8um653lpnqy">
          <CheckCircle2Icon className="h-12 w-12 text-success" aria-hidden="true" data-id="element-693" data-mp="17tpfkq0cn522" />
        </motion.div>
        <h1 className="mt-5 text-3xl font-bold tracking-tight text-ink" data-id="element-694" data-mp="1bvsr1en0q9nc">Bedankt voor je bestelling</h1>
        <p className="mt-3 text-sm text-muted" data-id="element-695" data-mp="3eh68wtzpkt4p">
          Bestelling <span className="font-semibold text-ink" data-id="element-696" data-mp="39j4r0ce0qzut">{orderNo}</span> is bevestigd. Je ontvangt een track-and-trace-link per e-mail zodra je pakket is verzonden.
        </p>
        <Link to="/shop" className="mt-8 inline-flex h-11 items-center rounded-lg bg-ink px-5 text-sm font-semibold text-white hover:bg-ink-soft" data-id="element-697" data-mp="34qle7hn5o8r2">
          Verder winkelen
        </Link>
      </div>;
  }
  if (items.length === 0) {
    return <div className="mx-auto flex max-w-lg flex-col items-center px-4 py-24 text-center" data-id="element-698" data-mp="2qs3stqkq38cb">
        <ShoppingCartIcon className="h-12 w-12 text-ink/20" aria-hidden="true" data-id="element-699" data-mp="2ltai25wtlvfu" />
        <h1 className="mt-5 text-3xl font-bold tracking-tight text-ink" data-id="element-700" data-mp="2gugzw06l9g9c">Je winkelwagen is leeg</h1>
        <p className="mt-3 text-sm text-muted" data-id="element-701" data-mp="k27354fwduph">Vind de juiste batterij in ons assortiment of laat ons batterijadvies helpen.</p>
        <div className="mt-8 flex gap-3" data-id="element-702" data-mp="f2kn6n8oq0cz">
          <Link to="/shop" className="inline-flex h-11 items-center rounded-lg bg-ink px-5 text-sm font-semibold text-white hover:bg-ink-soft" data-id="element-703" data-mp="f3vs4qdv9e5o">
            Bekijk producten
          </Link>
          <Link to="/#advisor" className="inline-flex h-11 items-center rounded-lg border border-line px-5 text-sm font-semibold text-ink hover:border-ink/40" data-id="element-704" data-mp="rk5b34ifkxql">
            Batterijadvies
          </Link>
        </div>
      </div>;
  }
  return <div className="mx-auto max-w-7xl px-4 pb-16 pt-5 sm:px-6 lg:px-8" data-id="element-705" data-mp="4lijh6mjj4z9">
      <Breadcrumbs items={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Winkelwagen'
    }]} data-id="element-706" data-mp="4nbnee5rrb3k" />
      <h1 className="mt-4 text-[28px] font-bold tracking-tight text-ink sm:text-4xl" data-id="element-707" data-mp="8n41f6dxm9gb">
        Winkelwagen <span className="text-muted" data-id="element-708" data-mp="dkm2ybpmdmea">({count})</span>
      </h1>

      <div className="mt-8 grid gap-10 lg:grid-cols-12" data-id="element-709" data-mp="im1nm2nvxqw5">
        <div className="lg:col-span-8" data-id="element-710" data-mp="iinnl12p1trv">
          <ul className="divide-y divide-line border-y border-line" data-id="element-711" data-mp="1p00xz9y9q46r">
            {items.map(item => <CartLineItem key={item.key} item={item} size="lg" data-id="element-712" data-mp="1tz40onqrzybk" />)}
          </ul>

          {addOns.length > 0 && <section aria-labelledby="addons-heading" className="mt-10" data-id="element-713" data-mp="32z804zplazmx">
              <h2 id="addons-heading" className="text-base font-bold text-ink" data-id="element-714" data-mp="2xzc4bnr1qeig">
                Vergeet dit niet
              </h2>
              <ul className="mt-4 grid gap-3 sm:grid-cols-2" data-id="element-715" data-mp="3bvfkwe8zyocg">
                {addOns.map(p => <li key={p.id} className="flex items-center gap-3 rounded-xl border border-line p-3" data-id="element-716" data-mp="3711y308ehpje">
                    <img src={p.image} alt="" className="h-14 w-14 rounded-lg bg-surface object-cover" data-id="element-717" data-mp="3ltn310f0vcg1" />
                    <div className="min-w-0 flex-1" data-id="element-718" data-mp="3uoa0naeg2khq">
                      <p className="truncate text-sm font-semibold text-ink" data-id="element-719" data-mp="3unrxdttd760b">{p.name}</p>
                      <p className="text-sm text-muted" data-id="element-720" data-mp="3qnzlb74rihq7">{formatPrice(p.price)}</p>
                    </div>
                    <button type="button" onClick={() => addItem(p, undefined, 1, false)} className="h-9 shrink-0 rounded-lg border border-ink px-3 text-xs font-semibold text-ink transition-colors duration-150 hover:bg-ink hover:text-white" data-id="element-721" data-mp="8huxcvrjgexi">
                      Toevoegen
                    </button>
                  </li>)}
              </ul>
            </section>}
        </div>

        <aside className="lg:col-span-4" aria-label="Overzicht bestelling" data-id="element-722" data-mp="i3e3cxkb30tb">
          <div className="rounded-2xl bg-surface p-6 lg:sticky lg:top-40" data-id="element-723" data-mp="4d0vswbbto4i">
            <h2 className="text-base font-bold text-ink" data-id="element-724" data-mp="940vg9btykap">Overzicht bestelling</h2>

            <form onSubmit={applyPromo} className="mt-5" data-id="element-725" data-mp="95u1pnij72i6">
              <label htmlFor="promo" className="text-xs font-medium text-muted" data-id="element-726" data-mp="3qltqn9tpenrj">
                Kortingscode
              </label>
              <div className="mt-1.5 flex gap-2" data-id="element-727" data-mp="3vhhhrurqxqzo">
                <div className="relative flex-1" data-id="element-728" data-mp="3hjki2phzx4gs">
                  <TagIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-729" data-mp="3hlx6nli9ed2t" />
                  <input id="promo" value={code} onChange={e => setCode(e.target.value)} disabled={applied} placeholder="bijv. AMPRA10" aria-invalid={!!promoError} aria-describedby="promo-msg" className="h-10 w-full rounded-lg border border-line bg-white pl-9 pr-3 text-sm uppercase text-ink placeholder:normal-case placeholder:text-muted focus:border-ink focus:outline-none disabled:opacity-60" data-id="element-730" data-mp="1s2ypgna4b5xe" />
                </div>
                <button type="submit" disabled={applied || !code} className="h-10 rounded-lg bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-soft disabled:cursor-not-allowed disabled:opacity-40" data-id="element-731" data-mp="212lsfdihwjsk">
                  Toepassen
                </button>
              </div>
              <p id="promo-msg" className="mt-1.5 min-h-[1rem] text-xs" aria-live="polite" data-id="element-732" data-mp="lopnxp2yeffi">
                {promoError && <span className="text-sale" data-id="element-733" data-mp="2x7vngf8u8inf">{promoError}</span>}
                {applied && <span className="font-medium text-success" data-id="element-734" data-mp="31c3923k01ihf">AMPRA10 toegepast — 10% korting (max. € 250)</span>}
              </p>
            </form>

            <dl className="mt-4 space-y-2.5 border-t border-line pt-4 text-sm" data-id="element-735" data-mp="3b13di2h9t630">
              <div className="flex justify-between" data-id="element-736" data-mp="3b37sbfgpjpi2">
                <dt className="text-muted" data-id="element-737" data-mp="3fzocxzop7lqe">Subtotaal</dt>
                <dd className="font-medium text-ink" data-id="element-738" data-mp="3kzj6yc3rsrfo">{formatPrice(subtotal)}</dd>
              </div>
              <AnimatePresence data-id="element-739" data-mp="3p1e3z4eo4inn">
                {applied && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.2
              }} className="flex justify-between overflow-hidden" data-id="element-740" data-mp="3p1xkqhsl1103">
                    <dt className="text-muted" data-id="element-741" data-mp="5e42jihc8ree">Korting</dt>
                    <dd className="font-medium text-success" data-id="element-742" data-mp="5cj7lrr436g0">−{formatPrice(discount)}</dd>
                  </motion.div>}
              </AnimatePresence>
              <div className="flex justify-between" data-id="element-743" data-mp="f346lgap6czg">
                <dt className="text-muted" data-id="element-744" data-mp="a52n4u9d0bng">Verzending</dt>
                <dd className="font-medium text-success" data-id="element-745" data-mp="a5m7ehmhfc4n">Gratis</dd>
              </div>
            </dl>
            <div className="mt-4 flex items-baseline justify-between border-t border-line pt-4" data-id="element-746" data-mp="3ile82t0ib7ni">
              <span className="text-base font-semibold text-ink" data-id="element-747" data-mp="3ekvgn02j03lu">Totaal</span>
              <span className="text-[28px] font-bold leading-none tracking-tight text-ink" data-id="element-748" data-mp="3sk3g7u2lpdqk">{formatPrice(total)}</span>
            </div>
            <p className="mt-1 text-right text-xs text-muted" data-id="element-749" data-mp="3ninyaavpznce">Incl. {formatPrice(vat)} btw</p>

            <button type="button" onClick={() => {
            setOrderNo(`AMP-${Math.floor(100000 + Math.random() * 900000)}`);
            clearCart();
          }} className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-volt text-sm font-bold text-ink transition-colors duration-150 hover:bg-volt-hover" data-id="element-750" data-mp="17r024hoj5yun">
              <LockIcon className="h-4 w-4" aria-hidden="true" data-id="element-751" data-mp="2bsj595b7r0mu" />
              Veilig afrekenen
            </button>
            <p className="mt-3 text-center text-xs text-muted" data-id="element-752" data-mp="2grmbgt2xjcwe">Binnen 1–2 werkdagen in huis</p>
            <ul className="mt-5 flex flex-wrap justify-center gap-1.5" aria-label="Betaalmethoden" data-id="element-753" data-mp="34hq3s4ao3q83">
              {paymentMethods.map(m => <li key={m} className="rounded border border-line bg-white px-2 py-0.5 text-[11px] font-bold text-ink" data-id="element-754" data-mp="2uqc8y9tnvela">
                  {m}
                </li>)}
            </ul>
          </div>
        </aside>
      </div>
    </div>;
}